import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

import {
  HOMEPAGE_BANNER_EVENT,
  isHomepageBannerShowing,
} from "../../utils/homepageVersion";
import { canRunBannerSweep, runBannerSweep } from "./utils/bannerSweep";
import { MAX_SWITCH_WAIT_MS } from "./utils/constants";
import { HOMEPAGE_LOADERS } from "./utils/homepageLoaders";
import { isPreloadAllowed, scheduleWhenIdle } from "./utils/preload";
import { otherVersion, withVersion } from "./utils/versions";

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Keeps both homepages ready for a student who can switch between them.
 *
 * The one they chose loads first, on its own. While the upgrade banner is on
 * screen — the window in which students actually switch — and once that
 * homepage's code is in and the browser is idle, the other one mounts hidden, so
 * its code and data are already there when they switch. Without the banner
 * nothing is preloaded; a switch loads the other homepage then.
 *
 * Switching plays the banner sweep and starts the other homepage at the top.
 * Nothing animates on a normal visit, only when the choice changes while the
 * homepage is on screen. A switch that beats the preload holds the old homepage
 * for at most MAX_SWITCH_WAIT_MS while the other one's code arrives.
 *
 * The preload fetches the other homepage's code first and only mounts it once
 * that worked, and a hidden homepage that fails is dropped (handleHiddenError)
 * and not tried again this visit — the one on screen must never pay for it.
 *
 * `activeVersion` is the homepage the student can see, for HomepageActiveContext.
 * During the sweep it is neither: the incoming one only becomes active once the
 * sweep is over, so its popups open afterwards, on top, as on a normal load.
 *
 * @param {Object} params
 * @param {"new"|"old"} params.version - the student's stored choice
 * @param {string} params.lineClassName - class for the sweep's line
 * @returns {{
 *   areaRef: React.MutableRefObject<HTMLElement|null>,
 *   shownVersion: "new"|"old",
 *   activeVersion: "new"|"old"|null,
 *   mountedVersions: Array<"new"|"old">,
 *   handleLoading: (version: "new"|"old") => void,
 *   handleLoaded: (version: "new"|"old") => void,
 *   handleHiddenError: (version: "new"|"old") => void,
 * }}
 *   handleLoading / handleLoaded — a homepage slot reports its code loading and loaded;
 *   handleHiddenError — the hidden slot threw
 */
const useHomepageSlots = ({ version, lineClassName }) => {
  const areaRef = useRef(null);
  const [shownVersion, setShownVersion] = useState(version);
  const [mountedVersions, setMountedVersions] = useState([version]);
  const [loadedVersions, setLoadedVersions] = useState([]);
  const [isSweeping, setIsSweeping] = useState(false);
  const [isBannerShowing, setIsBannerShowing] = useState(isHomepageBannerShowing);
  const [hasPreloadFailed, setHasPreloadFailed] = useState(false);
  // Refs for the async switch, which outlives the render that started it.
  const loadedRef = useRef([]);
  const waitersRef = useRef({});
  const latestVersionRef = useRef(version);
  const isMountedRef = useRef(true);
  latestVersionRef.current = version;

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    const sync = () => setIsBannerShowing(isHomepageBannerShowing());
    sync();
    window.addEventListener(HOMEPAGE_BANNER_EVENT, sync);
    return () => window.removeEventListener(HOMEPAGE_BANNER_EVENT, sync);
  }, []);

  const handleLoaded = useCallback((loaded) => {
    if (!isMountedRef.current) return;
    loadedRef.current = withVersion(loadedRef.current, loaded);
    setLoadedVersions(loadedRef.current);
    (waitersRef.current[loaded] ?? []).forEach((resolve) => resolve());
    delete waitersRef.current[loaded];
  }, []);

  const handleLoading = useCallback((loading) => {
    loadedRef.current = loadedRef.current.filter((item) => item !== loading);
    setLoadedVersions(loadedRef.current);
  }, []);

  const handleHiddenError = useCallback((failed) => {
    if (!isMountedRef.current) return;
    setHasPreloadFailed(true);
    loadedRef.current = loadedRef.current.filter((item) => item !== failed);
    setLoadedVersions(loadedRef.current);
    setMountedVersions((list) => list.filter((item) => item !== failed));
  }, []);

  const other = otherVersion(shownVersion);
  const isShownLoaded = loadedVersions.includes(shownVersion);
  const isOtherMounted = mountedVersions.includes(other);

  useEffect(() => {
    if (!isBannerShowing || !isShownLoaded || isOtherMounted) return undefined;
    if (hasPreloadFailed || !isPreloadAllowed()) return undefined;
    return scheduleWhenIdle(() =>
      HOMEPAGE_LOADERS[other]()
        .then(() => {
          if (isMountedRef.current) {
            setMountedVersions((list) => withVersion(list, other));
          }
        })
        .catch(() => {
          if (isMountedRef.current) setHasPreloadFailed(true);
        }),
    );
  }, [isBannerShowing, isShownLoaded, isOtherMounted, hasPreloadFailed, other]);

  // Every switch starts the other homepage at the top. Inside the sweep this
  // runs within flushSync, so the "new" snapshot is taken at the top.
  const hasShownRef = useRef(false);
  useLayoutEffect(() => {
    if (!hasShownRef.current) {
      hasShownRef.current = true;
      return;
    }
    window.scrollTo(0, 0);
  }, [shownVersion]);

  useLayoutEffect(() => {
    if (version === shownVersion) return;

    // Reads the latest choice, in case the student switched again mid-sweep.
    const show = () => {
      const next = latestVersionRef.current;
      setMountedVersions((list) => withVersion(list, next));
      setShownVersion(next);
    };

    if (!areaRef.current || !canRunBannerSweep()) {
      show();
      return;
    }

    const whenLoaded = (target) =>
      loadedRef.current.includes(target)
        ? Promise.resolve()
        : new Promise((resolve) => {
            waitersRef.current[target] = [
              ...(waitersRef.current[target] ?? []),
              resolve,
            ];
          });

    runBannerSweep({
      area: areaRef.current,
      lineClassName,
      update: async () => {
        if (!isMountedRef.current) return;
        flushSync(() => {
          setIsSweeping(true);
          show();
        });
        await Promise.race([
          whenLoaded(latestVersionRef.current),
          delay(MAX_SWITCH_WAIT_MS),
        ]);
      },
      onFinished: () => {
        if (isMountedRef.current) setIsSweeping(false);
      },
    });
  }, [version, shownVersion, lineClassName]);

  return {
    areaRef,
    shownVersion,
    activeVersion: isSweeping ? null : shownVersion,
    mountedVersions,
    handleLoading,
    handleLoaded,
    handleHiddenError,
  };
};

export default useHomepageSlots;
